import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Clock, MessageCircle, Gift, TrendingUp, Star, Heart, Zap, CheckCircle, Award, Users, ArrowRight, Target, BarChart3, Shield, Sparkles, Play } from "lucide-react";
import ProductPageLayout from "@/components/ProductPageLayout";
import RecognitionShowcase from "@/components/solutions/RecognitionShowcase";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import { SupademoModal } from "@/components/SupademoModal";
import TrustLogosSection from "@/components/trust/TrustLogosSection";
import HeroBadgesSection from "@/components/hero/HeroBadgesSection";
import G2RecognitionSection from "@/components/G2RecognitionSection";
import Testimonials from "@/components/Testimonials";
import { useState } from "react";
import { usePostHog } from "@/hooks/usePostHog";
const RecognizeWhatMatters = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSupademoModalOpen, setIsSupademoModalOpen] = useState(false);
  const { posthog } = usePostHog();
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.6
    }
  };
  const features = [{
    icon: <MessageCircle className="h-6 w-6" />,
    title: "Real-Time Recognition",
    description: "Give recognition in the moment when great work happens. No waiting for annual reviews.",
    color: "bg-assembly-blue",
    textColor: "text-assembly-blue"
  }, {
    icon: <Gift className="h-6 w-6" />,
    title: "Meaningful Rewards",
    description: "Points-based system with personalized rewards that employees actually want.",
    color: "bg-assembly-purple",
    textColor: "text-assembly-purple"
  }, {
    icon: <BarChart3 className="h-6 w-6" />,
    title: "Track Impact",
    description: "Measure engagement and culture health with powerful analytics and insights.",
    color: "bg-assembly-green",
    textColor: "text-assembly-green"
  }];
  const benefits = [{
    icon: <Target className="h-6 w-6" />,
    title: "87% Boost in Recognition",
    description: "Teams see dramatic increases in peer-to-peer recognition activities"
  }, {
    icon: <TrendingUp className="h-6 w-6" />,
    title: "94% Feel More Appreciated",
    description: "Employees report feeling significantly more valued and recognized"
  }, {
    icon: <Shield className="h-6 w-6" />,
    title: "Lower Turnover Rates",
    description: "Companies experience improved retention and employee satisfaction"
  }];
  const workflowSteps = [{
    step: "1",
    title: "Give Recognition",
    description: "Recognize teammates with points, values, and personal messages from anywhere",
    color: "from-assembly-blue to-blue-600"
  }, {
    step: "2",
    title: "Build Culture",
    description: "Recognition spreads across your organization, creating visible culture",
    color: "from-assembly-purple to-purple-600"
  }, {
    step: "3",
    title: "See Results",
    description: "Track engagement and cultural health with powerful analytics",
    color: "from-assembly-green to-green-600"
  }];
  return <ProductPageLayout title="Recognize What Matters - Employee Recognition Solutions" description="Build a culture of recognition with Assembly's peer-to-peer recognition platform. Increase engagement, improve retention, and celebrate what matters most." keywords="employee recognition, peer-to-peer recognition, workplace culture, employee engagement, recognition platform" ctaTitle="Ready to Build a Recognition Culture?" ctaDescription="See how Assembly helps companies create meaningful recognition programs that drive engagement and retention." ctaButtonText="Start Free Trial" ctaVariant="blue" showCTA={false}>
      {/* Hero Section */}
      <section className="relative pt-16 sm:pt-20 lg:pt-24 pb-12 sm:pb-16 lg:pb-20 bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50/30 overflow-hidden">
        {/* Subtle Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/8 via-assembly-blue/5 to-blue-400/4 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-assembly-purple/6 via-blue-500/5 to-slate-400/3 rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '1s'
        }}></div>
        </div>
        
        {/* Dot Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.15]">
          <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(59, 130, 246, 0.3) 2px, transparent 0)`,
          backgroundSize: '48px 48px'
        }}></div>
        </div>

        <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] relative px-6 lg:px-6 xl:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                  <span className="text-xs font-normal text-gray-900 tracking-wide uppercase">Free Rewards Platform</span>
                </div>
                <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                  <span className="text-xs font-normal text-gray-900 tracking-wide uppercase">Best Peer to Peer Employee Recognition</span>
                </div>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-gray-900 leading-tight mb-4 sm:mb-6">
                Recognize What Matters Retain Who Thrives
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Transform your workplace with peer-to-peer recognition, meaningful rewards, and engaging challenges that motivate everyone, drive performance, and boost retention.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200" 
                  onClick={() => setIsDemoModalOpen(true)}
                >
                  Book a Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="group bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-400 shadow-sm transition-all duration-200" 
                  onClick={() => {
                    posthog?.capture('see_it_in_action_clicked', {
                      page: '/solutions/recognize-what-matters',
                      section: 'hero',
                      button_text: 'See it in Action',
                      url: window.location.href,
                      opens_modal: true
                    });
                    setIsSupademoModalOpen(true);
                  }}
                >
                  See it in Action
                  <Play className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:scale-110" fill="currentColor" />
                </Button>
              </div>
            </div>
            <div className="mt-8 lg:mt-0">
              <RecognitionShowcase />
            </div>
          </div>
          
          {/* Trust Section at Bottom */}
          <motion.div className="text-center pt-8 lg:pt-12 mt-8 space-y-8" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.0
          }}>
            <p className="text-lg text-gray-600">
              The #1 Employee Recognition & Rewards Platform - Trusted by Leading Teams Worldwide
            </p>
            <HeroBadgesSection />
            <div className="pt-4">
              <TrustLogosSection />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Traditional Recognition Falls Flat Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {/* Left Content */}
            <div className="flex flex-col justify-between">
              <motion.div {...fadeInUp}>
              <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Why Traditional Recognition Falls Flat
                </h2>
                <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8">
                  Traditional recognition systems are outdated, invisible, and disconnected from real work.
                </p>
              </motion.div>
              
              {/* Illustration Image */}
              <motion.div 
                className="flex justify-center lg:justify-start"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <img 
                  src="/lovable-uploads/1cc1f406-df5a-4782-b9f2-24c295657490.png" 
                  alt="Remote work illustration"
                  className="w-full max-w-md h-auto"
                />
              </motion.div>
            </div>

            {/* Right Content - Problem Cards Grid */}
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Card 1 */}
              <div className="bg-rose-50 border border-rose-100 rounded-2xl p-8 relative min-h-[160px] flex flex-col">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 flex-1">
                  Recognition happens only during annual reviews
                </h3>
                <img 
                  src="/lovable-uploads/63f644ea-6b12-426d-bde3-6e856b3bd984.png" 
                  alt="Calendar icon"
                  className="absolute bottom-6 right-6 w-20 h-20 opacity-60"
                />
              </div>

              {/* Card 2 */}
              <div className="bg-rose-50 border border-rose-100 rounded-2xl p-8 relative min-h-[160px] flex flex-col">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 flex-1">
                  Day-to-day efforts go unnoticed
                </h3>
                <img 
                  src="/lovable-uploads/41c6cdc0-7982-4391-a651-ed9b7adf2878.png" 
                  alt="X icon"
                  className="absolute bottom-6 right-6 w-20 h-20 opacity-60"
                />
              </div>

              {/* Card 3 */}
              <div className="bg-rose-50 border border-rose-100 rounded-2xl p-8 relative min-h-[160px] flex flex-col">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 flex-1">
                  No visibility across teams
                </h3>
                <img 
                  src="/lovable-uploads/63c9e212-d002-42a5-aa44-96d57c610c5e.png" 
                  alt="Users icon"
                  className="absolute bottom-6 right-6 w-20 h-20 opacity-60"
                />
              </div>

              {/* Card 4 */}
              <div className="bg-rose-50 border border-rose-100 rounded-2xl p-8 relative min-h-[160px] flex flex-col">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 flex-1">
                  Not tied to company values
                </h3>
                <img 
                  src="/lovable-uploads/3af4c630-309b-4c5b-87ab-2959397128ee.png" 
                  alt="Link icon"
                  className="absolute bottom-6 right-6 w-20 h-20 opacity-60"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* G2 Recognition Section */}
      <G2RecognitionSection />

      {/* Powerful Peer Recognition Features Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Powerful <span className="text-blue-600">Peer Recognition</span> Features
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Assembly provides everything you need to build a culture of recognition and boost employee engagement.
            </p>
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div className="space-y-8" {...fadeInUp}>
              <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                Values-Driven
              </div>
              
              <div>
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Value-Based Recognition
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-700">
                      Celebrate what matters most.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-700">
                      Tie every shoutout to a core company value—so recognition reinforces your culture.
                    </p>
                  </div>
                </div>
                
                <div className="mt-8">
                  <a href="/recognition/peer-to-peer" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-lg transition-colors">
                    Learn More
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Dashboard Mockup */}
            <motion.div className="relative" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }}>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 lg:p-12">
                <img
                  src="/lovable-uploads/c663ce45-a9a8-4d64-be89-9fbacac96a19.png"
                  alt="Top 3 Recognized for Core Values - Leadership dashboard showing Jane Cooper (Compassionate, +500 points), Jenny Wilson (Initiative, +400 points), and Darrell Steward (Collaborative, +200 points)"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Public or Private Sharing Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div className="space-y-8" {...fadeInUp}>
              <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                Flexible Sharing
              </div>
              
              <div>
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Public or Private Sharing
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-700">
                      Recognize in your own way.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-700">
                      Let employees decide how they want to share appreciation—privately or across the org.
                    </p>
                  </div>
                </div>
                <div className="mt-8">
                  <a href="/recognition/peer-to-peer" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-lg transition-colors">
                    Learn More
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Image */}
            <motion.div className="relative" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }}>
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 lg:p-12">
                <img
                  src="/lovable-uploads/7242ff9a-7219-46da-a91e-87aa77b7632d.png"
                  alt="Recognition sharing options - showing Public Post (visible across company feed) and Private Post (visible to admins, tagged people and yourself)"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Live Company Feed Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div className="space-y-8" {...fadeInUp}>
              <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                Company-Wide Visibility
              </div>
              
              <div>
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Live Company Feed
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-700">
                      Bring visibility to everyday wins.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-700">
                      Shared recognitions help teams celebrate together and drive cross-functional engagement.
                    </p>
                  </div>
                </div>
                <div className="mt-8">
                  <a href="/recognition/peer-to-peer" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-lg transition-colors">
                    Learn More
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Image Placeholder */}
            <motion.div className="relative" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }}>
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 lg:p-12">
                <img
                  src="/lovable-uploads/b08a1660-99da-43e7-84bb-bd6a0083b10b.png"
                  alt="Live company feed showing recognition posts from Devon Lane, Jane Cooper, Dianne Russell, and Vikash R. with kudos, achievements, and team celebrations"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Points & Boosts Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div className="space-y-8" {...fadeInUp}>
              <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                Make It Count
              </div>
              
              <div>
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Points & Boosts
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-700">
                      Make recognition more meaningful.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-700">
                      Add redeemable points and boosts to turn words into rewards.
                    </p>
                  </div>
                </div>
                <div className="mt-8">
                  <a href="/recognition/peer-to-peer" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-lg transition-colors">
                    Learn More
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Image Placeholder */}
            <motion.div className="relative" initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }}>
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-8 lg:p-12">
                <img
                  src="/lovable-uploads/4610af94-772a-4beb-9ceb-95e7c6897006.png"
                  alt="Points and rewards system showing Darrell at 80% to next reward with 1200 points, surrounded by brand rewards like Nike, Visa, Amazon, AMC, and more"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nominate Top Performers Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Nominate your
                <br />
                <span className="text-assembly-purple">top performers</span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Engage employees with Employee nominations. Gather nominations from the whole team and make sure their accomplishments are celebrated.
              </p>
              <Button size="lg" className="bg-assembly-purple hover:bg-purple-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium text-base sm:text-lg" onClick={() => setIsDemoModalOpen(true)}>
                Explore Employee Nominations
              </Button>
            </motion.div>

            <motion.div className="relative" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }}>
              <img src="https://cdn.prod.website-files.com/6058ba87eec5713e5f80752a/610c09eb0d8ba932c7a3ad5c_employee-nominations.webp" alt="Employee nominations interface" className="w-full rounded-2xl shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Rewards Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <motion.div className="relative order-2 lg:order-1" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }}>
              <img src="https://cdn.prod.website-files.com/6058ba87eec5713e5f80752a/610c20d3a312f5661f66722b_rewards.webp" alt="Rewards marketplace" className="w-full rounded-2xl shadow-2xl" />
            </motion.div>

            <motion.div className="order-1 lg:order-2" {...fadeInUp}>
              <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Set up rewards that
                <br />
                <span className="text-assembly-pink">excite your team</span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                1,000s of gift card, charities, and personalized culture rewards to choose from. Reward points that people can redeem for a gift that makes them feel valued.
              </p>
              <Button size="lg" className="bg-assembly-pink hover:bg-pink-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium text-base sm:text-lg" onClick={() => setIsDemoModalOpen(true)}>
                Book A Demo
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Birthday & Anniversary Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Never miss a birthday
                <br />
                <span className="text-assembly-orange">or anniversary</span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Birthdays and work anniversaries sneak up on us all. The best way to never miss one is to automate it with a meaningful message, a fun GIF, and some points!
              </p>
              <Button size="lg" className="bg-assembly-orange hover:bg-orange-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium text-base sm:text-lg" onClick={() => setIsDemoModalOpen(true)}>
                Book A Demo
              </Button>
            </motion.div>

            <motion.div className="relative" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }}>
              <img src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/61a677d3d57e1e56ca034459_birthday-anniversary-automation.avif" alt="Birthday and anniversary automation" className="w-full rounded-2xl shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <motion.div className="relative order-2 lg:order-1" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }}>
              <img src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/6058ba87eec571460880765e_slack-and-ms-teams-integration.avif" alt="Slack and Microsoft Teams integration" className="w-full rounded-2xl shadow-2xl" />
            </motion.div>

            <motion.div className="order-1 lg:order-2" {...fadeInUp}>
              <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Never leave
                <br />
                <span className="text-assembly-blue">Slack or Teams</span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Don't force employees to go to another tool. Integrate Assembly directly into Slack or Microsoft teams and keep your recognition where your work already happens.
              </p>
              <Button size="lg" className="bg-assembly-blue hover:bg-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium text-base sm:text-lg" onClick={() => setIsDemoModalOpen(true)}>
                Book A Demo
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Insights Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Create a single
                <br />
                <span className="text-assembly-green">source of truth</span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Consistent data with powerful insights
              </p>
            </motion.div>

            <motion.div className="relative" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }}>
              <img src="/lovable-uploads/f7f341c0-f4e4-4dd0-be35-f8bfaad1446c.png" alt="Recognition insights dashboard showing 137 recognitions, 215 points received, 185 recognition rate, and 350 participants with trending analytics" className="w-full rounded-2xl shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Recognition Banner */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-assembly-blue to-assembly-purple">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center" {...fadeInUp}>
            <h2 className="text-4xl sm:text-6xl lg:text-8xl font-bold text-white mb-6 sm:mb-8 tracking-tight">
              Recognition and Rewards
            </h2>
            <p className="text-xl sm:text-2xl text-blue-100 mb-8 sm:mb-12 max-w-3xl mx-auto">
              Celebrate achievements, motivate teams, and drive retention with meaningful recognition and instant rewards
            </p>
            <Button size="lg" className="bg-white text-assembly-blue hover:bg-gray-100 px-8 sm:px-10 py-4 sm:py-6 text-lg sm:text-xl rounded-xl shadow-lg hover:shadow-xl transition-all duration-300" onClick={() => {
              posthog?.capture('see_it_in_action_clicked', {
                page: '/solutions/recognize-what-matters',
                section: 'recognition_banner',
                button_text: 'See It In Action',
                url: window.location.href,
                opens_modal: true
              });
              setIsDemoModalOpen(true);
            }}>
              See It In Action
              <ArrowRight className="ml-2 h-5 w-5 sm:h-6 sm:w-6" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Assembly's Recognition & Rewards platform.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  How does peer-to-peer recognition work in Assembly?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Assembly makes recognition simple and instant. Employees can recognize teammates directly through Slack, Teams, or the web app by giving points, tagging company values, and adding personal messages. Recognition is visible across your organization, creating a culture of appreciation.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  What types of rewards can employees redeem?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Employees can choose from thousands of rewards including gift cards (100+ countries), Amazon products, company swag, travel experiences, charitable donations, and custom company perks. Everything from extra PTO days to team lunches with leadership.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  Can we tie recognition to our company values?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Absolutely. Every recognition can be tagged to specific company values, making culture reinforcement automatic. You'll see which values resonate most, who embodies them, and how recognition drives the behaviors that matter most to your organization.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  How do challenges and milestones work?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Create custom challenges to drive specific behaviors or celebrate milestones like birthdays, work anniversaries, and achievements. Automation ensures nothing gets missed, and employees receive recognition exactly when it matters most.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  What integrations are available?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Assembly integrates with Slack, Microsoft Teams, BambooHR, Rippling, Gusto, Workday, and more. Recognition happens where your team already works, and HR data syncs automatically to keep everything up to date.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  How does Assembly improve employee retention?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Recognition is one of the strongest drivers of retention. When employees feel valued and appreciated consistently, they're more engaged, motivated, and loyal. Assembly makes recognition frequent, visible, and tied to real impact—reducing turnover and building stronger teams.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <AccordionTrigger className="px-6 py-5 hover:bg-gray-50">
                <span className="text-left font-semibold text-gray-900">
                  Is Assembly suitable for remote and hybrid teams?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-gray-600 leading-relaxed">
                  Yes. Assembly is built for distributed teams. Recognition works seamlessly across locations, departments, and time zones. Even deskless employees can receive rewards via email or SMS without logging in.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      
      <DemoBookingModal open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen} />
      <SupademoModal open={isSupademoModalOpen} onOpenChange={setIsSupademoModalOpen} />
    </ProductPageLayout>;
};
export default RecognizeWhatMatters;